// ==================== DASHBOARD PAGE ====================
import { state } from '../state.js';
import { dbClient } from '../db.js';
import {
  findNextCalendarEntry,
  monthlyBudgetExistsForEntry,
  getSubmissionStatus,
  getDailyReconciliationStatus,
  formatDisplayDate,
  todayDateStr
} from '../utils/budgetCalendar.js';
import { sumBucketBalancesToUsd, formatUsdDisplay } from '../utils/currency.js';
import {
  computeBucketStatusRow,
  filterBucketsByScope,
  bucketHasMoney
} from '../utils/financialStatusHelpers.js';
import {
  fetchPendingTransfersForUser
} from '../utils/transferActions.js';
import {
  acceptTransferFromDashboard,
  rejectTransferFromDashboard
} from './transfer.js';

import { teamAccessLabel } from '../utils/roleLabels.js';
import { getBudgetStatus, BUDGET_STATUS } from '../utils/budgetStatus.js';

function formatUsd(amount) {
  return '$' + formatUsdDisplay(amount);
}

export function getDashboardPage() {
  const teamName = state.currentTeam?.team_name || 'your team';
  const userName = state.user?.name || 'User';

  return `
    <div style="margin-bottom: 25px; display: flex; flex-direction: column; gap: 4px;">
      <h1 class="page-title" style="display: none;">Dashboard</h1>
      <p class="dash-welcome" style="font-size: 1.1rem; color: var(--text-secondary); margin: 0; font-weight: 500;">Welcome, ${escapeHtmlAttr(userName)}!</p>
      <p style="font-size: 0.85rem; color: var(--text-muted); margin: 0;">Operational overview for <strong>${escapeHtmlAttr(teamName)}</strong></p>
    </div>

    <div class="stats-grid dash-stats" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px; margin-bottom: 25px;">
      <!-- Team Balance -->
      <div class="card card-hover glass" style="padding: 20px; border-radius: var(--radius); display: flex; flex-direction: column; gap: 15px; border: 1px solid var(--border); box-shadow: var(--shadow); position: relative; overflow: hidden;">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <span style="font-size: 0.85rem; font-weight: 600; color: var(--text-secondary);">Team Balance</span>
          <div style="width: 32px; height: 32px; border-radius: 8px; background: rgba(59, 130, 246, 0.1); color: var(--primary); display: flex; align-items: center; justify-content: center; font-size: 0.95rem;">
            <i class="fa-solid fa-wallet"></i>
          </div>
        </div>
        <div>
          <h3 id="dashBalance" style="font-size: 1.8rem; font-weight: 700; margin: 0; color: var(--text);">—</h3>
          <p style="font-size: 0.75rem; color: var(--text-muted); margin: 4px 0 0 0;">Total USD across money buckets</p>
        </div>
      </div>

      <!-- Expenses Blob -->
      <div class="card card-hover glass" style="padding: 20px; border-radius: var(--radius); display: flex; flex-direction: column; gap: 15px; border: 1px solid var(--border); box-shadow: var(--shadow); position: relative; overflow: hidden;">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <span style="font-size: 0.85rem; font-weight: 600; color: var(--text-secondary);">Logged Expenses</span>
          <div style="width: 32px; height: 32px; border-radius: 8px; background: rgba(239, 68, 68, 0.1); color: var(--error); display: flex; align-items: center; justify-content: center; font-size: 0.95rem;">
            <i class="fa-solid fa-receipt"></i>
          </div>
        </div>
        <div>
          <h3 id="dashExpenses" style="font-size: 1.8rem; font-weight: 700; margin: 0; color: var(--text);">—</h3>
          <p style="font-size: 0.75rem; color: var(--text-muted); margin: 4px 0 0 0;"><span id="dashExpPeriod">All time</span></p>
        </div>
      </div>

      <!-- Outstanding Amount -->
      <div class="card card-hover glass" style="padding: 20px; border-radius: var(--radius); display: flex; flex-direction: column; gap: 15px; border: 1px solid var(--border); box-shadow: var(--shadow); position: relative; overflow: hidden;">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <span style="font-size: 0.85rem; font-weight: 600; color: var(--text-secondary);">Outstanding Amount</span>
          <div style="width: 32px; height: 32px; border-radius: 8px; background: rgba(245, 158, 11, 0.1); color: #f59e0b; display: flex; align-items: center; justify-content: center; font-size: 0.95rem;">
            <i class="fa-solid fa-scale-unbalanced"></i>
          </div>
        </div>
        <div>
          <h3 id="dashOutstanding" style="font-size: 1.8rem; font-weight: 700; margin: 0; color: var(--text);">—</h3>
          <p style="font-size: 0.75rem; color: var(--text-muted); margin: 4px 0 0 0;">Allocated Income minus Expenses</p>
        </div>
      </div>

      <!-- Allocated Income -->
      <div class="card card-hover glass" style="padding: 20px; border-radius: var(--radius); display: flex; flex-direction: column; gap: 15px; border: 1px solid var(--border); box-shadow: var(--shadow); position: relative; overflow: hidden;">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <span style="font-size: 0.85rem; font-weight: 600; color: var(--text-secondary);">Allocated Income</span>
          <div style="width: 32px; height: 32px; border-radius: 8px; background: rgba(16, 185, 129, 0.1); color: var(--success); display: flex; align-items: center; justify-content: center; font-size: 0.95rem;">
            <i class="fa-solid fa-arrow-trend-up"></i>
          </div>
        </div>
        <div>
          <h3 id="dashIncome" style="font-size: 1.8rem; font-weight: 700; margin: 0; color: var(--text);">—</h3>
          <p style="font-size: 0.75rem; color: var(--text-muted); margin: 4px 0 0 0;">Funds registered this period</p>
        </div>
      </div>

      <!-- Booked Expenses -->
      <div class="card card-hover glass" style="padding: 20px; border-radius: var(--radius); display: flex; flex-direction: column; gap: 15px; border: 1px solid var(--border); box-shadow: var(--shadow); position: relative; overflow: hidden;">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <span style="font-size: 0.85rem; font-weight: 600; color: var(--text-secondary);">Booked Expenses</span>
          <div style="width: 32px; height: 32px; border-radius: 8px; background: rgba(239, 68, 68, 0.1); color: var(--danger); display: flex; align-items: center; justify-content: center; font-size: 0.95rem;">
            <i class="fa-solid fa-arrow-trend-down"></i>
          </div>
        </div>
        <div>
          <h3 id="dashBooked" style="font-size: 1.8rem; font-weight: 700; margin: 0; color: var(--text);">—</h3>
          <p style="font-size: 0.75rem; color: var(--text-muted); margin: 4px 0 0 0;">Outflows logged against budgets</p>
        </div>
      </div>

      <!-- Next Submission -->
      <div class="card card-hover glass" id="dashSubmissionCard" style="padding: 20px; border-radius: var(--radius); display: flex; flex-direction: column; gap: 15px; border: 1px solid var(--border); box-shadow: var(--shadow); position: relative; overflow: hidden;">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <span style="font-size: 0.85rem; font-weight: 600; color: var(--text-secondary);" id="dashSubmissionLabel">Next Submission</span>
          <div style="width: 32px; height: 32px; border-radius: 8px; background: rgba(245, 158, 11, 0.1); color: var(--warning); display: flex; align-items: center; justify-content: center; font-size: 0.95rem;">
            <i class="fa-solid fa-calendar-day"></i>
          </div>
        </div>
        <div>
          <h3 id="dashNextSubmission" style="font-size: 1.8rem; font-weight: 700; margin: 0; color: var(--text);">—</h3>
          <p style="font-size: 0.75rem; color: var(--text-muted); margin: 4px 0 0 0;">Budget plan submission deadline</p>
        </div>
      </div>
    </div>

    <div class="card dash-alerts-card glass" style="padding: 24px; border-radius: var(--radius); border: 1px solid var(--border); box-shadow: var(--shadow);">
      <h2 style="font-size: 1.15rem; font-weight: 700; color: var(--text); margin: 0 0 15px 0; display: flex; align-items: center; gap: 8px;">
        <i class="fa-solid fa-circle-exclamation text-primary"></i>
        <span>Operational Alerts</span>
      </h2>
      <div id="dashAlerts" class="dash-alerts">
        <p class="dash-alerts-loading" style="color: var(--text-muted); font-size: 0.9rem;">Loading alerts…</p>
      </div>
    </div>
  `;
}

function escapeHtmlAttr(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export async function initDashboardPage() {
  const teamId = state.currentTeam?.team_id;
  if (!teamId) return;

  window.acceptDashboardTransfer = async (transferId) => {
    const ok = await acceptTransferFromDashboard(transferId);
    if (ok) await initDashboardPage();
  };
  window.rejectDashboardTransfer = async (transferId) => {
    const ok = await rejectTransferFromDashboard(transferId);
    if (ok) await initDashboardPage();
  };

  const today = todayDateStr();

  try {
    const [
      bucketsRes,
      budgetsRes,
      incomeRes,
      expensesRes,
      calendarRes,
      reconSubmissionsRes,
      ratesRes,
      transfersRes
    ] = await Promise.all([
      dbClient.from('buckets').select('*').eq('team_id', teamId).eq('is_deleted', false),
      dbClient.from('budget_plans').select('id, status, budget_type, calendar_entry_id, budget_period_date').eq('team_id', teamId).eq('is_deleted', false),
      dbClient.from('income').select('amount_usd, budget_allocations, bucket_id, local_amount, currency, date, is_deleted').eq('team_id', teamId).eq('is_deleted', false),
      dbClient.from('expenses').select('budget_id, usd_amount, bucket_id, local_amount, currency, date, is_deleted').eq('team_id', teamId).eq('is_deleted', false),
      dbClient.from('budget_calendar_entries').select('*').eq('is_deleted', false).order('submission_deadline'),
      dbClient.from('reconciliation_submissions').select('scope, user_id, reconciliation_date').eq('team_id', teamId).eq('is_deleted', false).eq('reconciliation_date', today),
      dbClient.from('exchange_rates').select('*').is('team_id', null).eq('is_deleted', false).order('date', { ascending: false }),
      dbClient.from('transfers').select('*').eq('team_id', teamId).eq('is_deleted', false)
    ]);

    const buckets = bucketsRes.data || [];
    const rates = ratesRes.data || [];
    const budgets = budgetsRes.data || [];
    const income = incomeRes.data || [];
    const expenses = expensesRes.data || [];
    const calendarEntries = calendarRes.error ? [] : (calendarRes.data || []);
    const reconSubmissions = reconSubmissionsRes.error ? [] : (reconSubmissionsRes.data || []);
    const transfers = transfersRes.data || [];

    const teamBuckets = filterBucketsByScope(buckets, 'team');
    const { totalUsd: totalBalanceUsd, missingRates, missingCurrency, breakdown } = sumBucketBalancesToUsd(teamBuckets, rates);
    const hasForeignBuckets = teamBuckets.some(b => String(b.currency || '').trim() && b.currency !== 'USD');

    const balanceEl = document.getElementById('dashBalance');
    const balanceLabel = document.getElementById('dashBalanceLabel');
    if (balanceEl) {
      balanceEl.textContent = formatUsd(totalBalanceUsd);
      const titleParts = breakdown.length ? [breakdown.join('\n')] : [];
      if (missingRates.length) {
        titleParts.push(`Excluded (no rate): ${missingRates.join(', ')}`);
      }
      if (missingCurrency.length) {
        titleParts.push(`Excluded (currency not set): ${missingCurrency.join(', ')}`);
      }
      balanceEl.title = titleParts.join('\n\n') || 'Team operational bucket balances';
    }
    if (balanceLabel) {
      balanceLabel.textContent = hasForeignBuckets
        ? 'Team balance (USD equiv.)'
        : 'Team balance (USD)';
    }

    const currentBudgets = budgets.filter(b => {
      const s = getBudgetStatus(b);
      return s === BUDGET_STATUS.APPROVED || s === BUDGET_STATUS.PAID || s === BUDGET_STATUS.RECEIVED;
    });
    const currentBudgetIds = new Set(currentBudgets.map(b => b.id));

    let allocatedIncome = 0;
    income.forEach(rec => {
      (rec.budget_allocations || []).forEach(a => {
        if (currentBudgetIds.has(a.budget_id)) {
          allocatedIncome += parseFloat(a.amount_usd) || 0;
        }
      });
    });
    const incomeEl = document.getElementById('dashIncome');
    if (incomeEl) incomeEl.textContent = formatUsd(allocatedIncome);

        const allTimeExpenses = expenses.reduce((sum, e) => sum + (parseFloat(e.usd_amount) || 0), 0);
    const expensesEl = document.getElementById('dashExpenses');
    if (expensesEl) expensesEl.textContent = formatUsd(allTimeExpenses);

    const bookedExpenses = expenses
      .filter(e => currentBudgetIds.has(e.budget_id))
      .reduce((sum, e) => sum + (parseFloat(e.usd_amount) || 0), 0);
    const bookedEl = document.getElementById('dashBooked');
    if (bookedEl) bookedEl.textContent = formatUsd(bookedExpenses);

    const outstanding = allocatedIncome - bookedExpenses;
    const outEl = document.getElementById('dashOutstanding');
    if (outEl) outEl.textContent = formatUsd(outstanding);

    const currentEntry = findNextCalendarEntry(calendarEntries, today);
    const hasMonthlyForCurrent = monthlyBudgetExistsForEntry(budgets, currentEntry);
    const submissionStatus = getSubmissionStatus(currentEntry, hasMonthlyForCurrent, today);

    const nextEl = document.getElementById('dashNextSubmission');
    const submissionCard = document.getElementById('dashSubmissionCard');
    const submissionLabel = document.getElementById('dashSubmissionLabel');

    if (submissionCard) {
      submissionCard.classList.remove('stat-card--gold', 'stat-card--success', 'stat-card--danger');
    }

    if (nextEl) {
      if (!currentEntry) {
        nextEl.textContent = '—';
        nextEl.title = 'No calendar configured';
        if (submissionLabel) submissionLabel.textContent = 'Next Budget Submission';
        if (submissionCard) submissionCard.classList.add('stat-card--gold');
      } else if (hasMonthlyForCurrent) {
        nextEl.textContent = formatDisplayDate(currentEntry.submission_deadline);
        nextEl.title = `Submitted for ${formatDisplayDate(currentEntry.budget_period_date)}`;
        if (submissionLabel) {
          submissionLabel.textContent = `Submitted — ${formatDisplayDate(currentEntry.budget_period_date)}`;
        }
        if (submissionCard) submissionCard.classList.add('stat-card--success');
      } else if (submissionStatus.isOverdue) {
        const delay = Math.abs(submissionStatus.days);
        nextEl.textContent = delay === 1 ? 'Delayed 1 day' : `Delayed ${delay} days`;
        nextEl.title = `Was due ${formatDisplayDate(currentEntry.submission_deadline)}`;
        if (submissionLabel) {
          submissionLabel.textContent = `Due ${formatDisplayDate(currentEntry.submission_deadline)}`;
        }
        if (submissionCard) submissionCard.classList.add('stat-card--danger');
      } else if (submissionStatus.days === 0) {
        nextEl.textContent = 'Due today';
        nextEl.title = `Submit by ${formatDisplayDate(currentEntry.submission_deadline)}`;
        if (submissionLabel) {
          submissionLabel.textContent = `Due ${formatDisplayDate(currentEntry.submission_deadline)}`;
        }
        if (submissionCard) submissionCard.classList.add('stat-card--danger');
      } else {
        nextEl.textContent = `${submissionStatus.days}d`;
        nextEl.title = `Submit by ${formatDisplayDate(currentEntry.submission_deadline)}`;
        if (submissionLabel) {
          submissionLabel.textContent = `Due ${formatDisplayDate(currentEntry.submission_deadline)}`;
        }
        if (submissionCard) submissionCard.classList.add('stat-card--gold');
      }
    }

    const alerts = [];

    // Pending transfers awaiting this user's confirmation
    try {
      const pendingTransfers = await fetchPendingTransfersForUser(teamId, state.user?.id);
      const { data: bucketList } = await dbClient
        .from('buckets')
        .select('id, name')
        .eq('team_id', teamId)
        .eq('is_deleted', false);
      const bucketName = Object.fromEntries((bucketList || []).map(b => [b.id, b.name]));

      pendingTransfers.forEach(t => {
        const fromName = bucketName[t.from_bucket_id] || 'Source';
        const amount = parseFloat(t.amount) || 0;
        const isOhf = t.pending_step === 'ohf';
        alerts.unshift({
          level: 'warning',
          icon: isOhf ? '✅' : '💸',
          title: isOhf ? 'Approve cross-team transfer' : 'Confirm money received',
          message: `${amount.toFixed(2)} ${t.currency || ''} from ${fromName} — ${t.description || ''}`,
          transferId: t.id,
          isTransfer: true,
          transferIsOhf: isOhf
        });
      });
    } catch (pendingErr) {
      console.warn('Pending transfers load:', pendingErr);
    }

    // Pending approval requests awaiting this user's turn
    try {
      const { fetchApprovalInboxRaw } = await import('../utils/approvalEngine.js');
      const { getUserApprovalRoleCodes, clarifyRoleFromStatus } = await import('../utils/approvalAccess.js');
      const approvals = await fetchApprovalInboxRaw();
      
      for (const req of approvals) {
        const userCodes = await getUserApprovalRoleCodes(state.user?.id, req.team_id);
        const upperCodes = userCodes.map(c => String(c).toUpperCase());
        const currentRole = String(req.current_role_code || '').toUpperCase();
        
        const isClarifyTarget = req.status.startsWith('CLARIFY-') && 
          (clarifyRoleFromStatus(req.status) === 'REQUESTER' 
            ? req.created_by === state.user?.id 
            : upperCodes.includes(String(clarifyRoleFromStatus(req.status)).toUpperCase()));
          
        const isCurrentAssignee = req.current_role_code && upperCodes.includes(currentRole);
        
        if (isCurrentAssignee || isClarifyTarget) {
          alerts.unshift({
            level: 'warning',
            icon: '📝',
            title: 'Approval request pending',
            message: `${req.request_number}: ${req.title || 'Untitled'} — Awaiting ${req.current_role_code}`,
            action: { page: 'approval-portal', label: 'Go to Inbox' }
          });
        }
      }
    } catch (appErr) {
      console.warn('Dashboard approvals load:', appErr);
    }

    if (currentEntry && !hasMonthlyForCurrent) {
      alerts.push({
        level: 'danger',
        icon: '📋',
        title: 'Monthly budget due',
        message: submissionStatus.message,
        action: { page: 'create-budget', label: 'Create budget' }
      });
    }

    const totalReceived = income.reduce((sum, r) => sum + (parseFloat(r.amount_usd) || 0), 0);
    const remaining = totalReceived - allTimeExpenses;
    if (totalReceived > 0 && remaining / totalReceived < 0.25) {
      const pct = Math.round((remaining / totalReceived) * 100);
      alerts.push({
        level: 'danger',
        icon: '💰',
        title: 'Low balance',
        message: `Only ${pct}% of received funds remain (${formatUsd(remaining)} of ${formatUsd(totalReceived)})`,
        action: { page: 'income-manager', label: 'View income' }
      });
    }

    const reconRequired = filterBucketsByScope(buckets, 'all').some(b => bucketHasMoney(
      computeBucketStatusRow(b, today, today, income, expenses, transfers, buckets, rates)
    ));
    const submittedToday = reconSubmissions.some(s => s.scope === 'all' || s.scope === 'team');
    const reconStatus = getDailyReconciliationStatus({
      submittedToday,
      required: reconRequired
    }, today);

    alerts.push({
      level: reconStatus.level,
      icon: '📊',
      title: 'Daily reconciliation',
      message: reconStatus.message,
      action: { page: 'financial-status', label: 'Financial status' }
    });

    if (alerts.length === 0) {
      alerts.push({ level: 'success', icon: '✓', title: 'All clear', message: 'No alerts at this time' });
    }

    renderAlerts(alerts);
  } catch (e) {
    console.error('Dashboard load error:', e);
    const alertsEl = document.getElementById('dashAlerts');
    if (alertsEl) {
      alertsEl.innerHTML = `<div class="dash-alert dash-alert--danger"><strong>Error loading dashboard</strong><span>${e.message}</span></div>`;
    }
  }
}

function renderAlerts(alerts) {
  const container = document.getElementById('dashAlerts');
  if (!container) return;

  container.innerHTML = alerts.map(a => `
    <div class="dash-alert dash-alert--${a.level}">
      <div class="dash-alert-icon" aria-hidden="true">${a.icon}</div>
      <div class="dash-alert-body">
        <strong>${a.title}</strong>
        <span>${a.message}</span>
      </div>
      ${a.isTransfer ? `
        <div class="dash-alert-actions">
          <button type="button" class="dash-alert-action dash-alert-action--accept" onclick="window.acceptDashboardTransfer('${a.transferId}')">${a.transferIsOhf ? 'Approve' : 'Accept'}</button>
          <button type="button" class="dash-alert-action dash-alert-action--reject" onclick="window.rejectDashboardTransfer('${a.transferId}')">Reject</button>
        </div>
      ` : ''}
      ${a.action && !a.isTransfer ? `<button type="button" class="dash-alert-action" onclick="window.showPage('${a.action.page}')">${a.action.label}</button>` : ''}
    </div>
  `).join('');
}
